<template>
  <div
    v-if="data.chartDataAvailable"
    class="i-report-snapshot"
  >
    <div class="top">
      <div>Here's a snapshot of <strong>your results.</strong></div>
    </div>
    <div class="bottom">
      <div class="card">
        <div>See What Drives Your Work Style</div>
        <iRadarChart
          class="chart"
          v-bind="data.capboiChart"
        />
        <iBaseButton
          class="button"
          v-bind="buttonStyle"
          text="View Full Personality Profile"
          @clicked="$emit('scroll-to-personality')"
        />
      </div>
      <div class="card">
        <div>Explore Your Personalized Career Matches</div>
        <div class="goals">
          <div
            v-for="goal in topGoals"
            :key="goal"
            class="goal"
          >
            <img :src="IconGoal" />{{ goal }}
          </div>
        </div>
        <iBaseButton
          class="button"
          v-bind="buttonStyle"
          text="See Career Matches"
          @clicked="$emit('scroll-to-careers')"
        />
      </div>
      <div class="card">
        <div>Find Out What You Could Be Earning</div>
        <div class="earnings">
          <div class="figure">{{ salaryIncrease }}% increase</div>
        </div>
        <iBaseButton
          class="button"
          v-bind="buttonStyle"
          text="Unlock My Earning Potential"
          @clicked="$emit('scroll-to-earnings')"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, reactive } from "vue"
import { useReportStore } from "@/stores/report"
import { useUserStore } from "@/stores/user"
import { amethyst500, bluePurpleGradient, white, black800, black200 } from "@/styles/colors"
import { breakpointLg } from "@/styles/variables"

import useRandomNumber from "@/composables/useRandomNumber"
import useIsScreenSize from "@/composables/useIsScreenSize"

import iRadarChart from "@/components/global/RadarChart.vue"
import iBaseButton from "@/components/base/BaseButton.vue"

// ASSETS
import IconGoal from "@/assets/icons/icon-goal-purple.svg"

// TYPES
interface ChartModel {
  canvasId: string
  chartId: string
  colorMe: string
  gridColor: string
  labels: (string | string[])[]
  labelColor: string
  labelHighlightColor: string
  maxDiameter: string
  range: { minimum: number; maximum: number }
  scoresMe: number[]
  steps: number
}

// EMITS
defineEmits(["scroll-to-personality", "scroll-to-careers", "scroll-to-earnings"])

// STORES AND COMPOSABLES
const reportStore = useReportStore()
const userStore = useUserStore()
const isLargerThanTablet = useIsScreenSize(breakpointLg)

// DATA
const data = reactive({
  chartDataAvailable: false,
  capboiChart: {} as ChartModel
})

//COMPUTED
const buttonStyle = computed(() => {
  return {
    backgroundImage: bluePurpleGradient,
    borderRadius: "8px",
    fontWeight: "500",
    textColor: white,
    width: "100%",
    padding: isLargerThanTablet.value ? "8px 70px" : "10px 6px",
    size: "medium"
  }
})
const topGoals = computed(() => reportStore.reportData?.results?.topGoals.slice(0, 3))
const salaryIncrease = computed(() => {
  const potentialPercent = reportStore.salaryStatistics.comparisons.currentPotentialPercent
  const randomIncrease = useRandomNumber(userStore.getFullName || "Full Name", 20, 35)

  return potentialPercent < 0 ? -potentialPercent : randomIncrease
})

// FUNCTIONS
function setChartsData() {
  data.capboiChart = {
    colorMe: "#d5adffc7",
    maxDiameter: "350px",
    steps: 4,
    gridColor: black200,
    labelColor: black800,
    labelHighlightColor: amethyst500,
    canvasId: "capboiCanvas",
    chartId: "capboiChart",
    labels: reportStore.getCapboiLabels,
    range: reportStore.getCapboiRange,
    scoresMe: reportStore.getCapboiScores
  } as ChartModel

  data.chartDataAvailable = true
}

// LIFECYCLE HOOKS
onMounted(() => {
  setChartsData()
})
</script>

<style scoped lang="scss">
.i-report-snapshot {
  @include flex(column, center, center, $gap: 40px);

  padding: 50px 15px;

  @media screen and (min-width: $breakpoint-md) {
    padding: 60px 15px;
  }

  .top {
    text-align: center;

    @include display-md;

    @media screen and (min-width: $breakpoint-md) {
      @include display-lg;
    }
  }

  .bottom {
    @include flex(row, center, stretch, wrap, $gap: 24px);
    @include text-lg;

    width: 100%;
    font-weight: 700;

    .card {
      @include flex(column, space-between, stretch, $gap: 0);
      @include border-radius-md;

      text-align: center;
      width: 100%;
      max-width: 410px;
      background-color: $white;
      padding: 25px;

      @media screen and (min-width: $breakpoint-md) {
        padding: 40px;
      }

      &:not(:first-child) {
        gap: 24px;
      }

      .chart {
        display: block;
      }

      .goals {
        @include flex(column, center, stretch, $gap: 8px);
        @include text-sm;

        font-weight: 600;

        .goal {
          @include flex(row, start, center, $gap: 15px);
          @include border-radius-md;

          text-align: left;
          padding: 24px;
          box-shadow: 0 0 15px 15px #c5c5c516;
        }
      }

      .earnings {
        position: relative;
        background-image: url("@/assets/welcome/earning-graph.avif");
        background-size: cover;
        background-repeat: no-repeat;
        background-position: center;
        width: 100%;
        max-width: 330px;
        height: 266px;

        .figure {
          @include text-sm;

          font-weight: 600;
          position: absolute;
          top: 18%;
          left: 42%;

          @media screen and (min-width: $breakpoint-sm) {
            top: 47px;
            left: 140px;
          }
        }
      }
    }
  }
}
</style>
